import DocumentType from '@/enums/document-type.enum'
import { TAG_LABELS, TAG_TYPES } from '@/modules/file/constants'

const CANCELLATION_DOCUMENT_TYPE_CODES = new Set([
  '81',
  '83',
  '261',
  '262',
  '296',
  '308',
  '381',
  '389',
  '396',
  '420',
  '458',
  '532',
])

const CANCELLATION_TEXT_PATTERNS = [
  /storno/i,
  /stornorechnung/i,
  /cancellation/i,
  /cancelled/i,
  /credit\s*note/i,
  /creditnote/i,
]

const unwrapValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return unwrapValue(value.value)
  }

  return value
}

const collectValues = (value) => {
  const unwrapped = unwrapValue(value)

  if (Array.isArray(unwrapped)) {
    return unwrapped.flatMap(collectValues)
  }

  if (unwrapped && typeof unwrapped === 'object') {
    return Object.values(unwrapped).flatMap(collectValues)
  }

  return unwrapped === null || unwrapped === undefined || unwrapped === ''
    ? []
    : [unwrapped]
}

export const isCancellationInvoiceDocumentType = (documentType) => {
  return collectValues(documentType).some((value) => {
    const normalizedValue = `${value}`.trim()
    return CANCELLATION_DOCUMENT_TYPE_CODES.has(normalizedValue)
      || CANCELLATION_TEXT_PATTERNS.some(pattern => pattern.test(normalizedValue))
  })
}

/**
 * FLAG:useDocuAi — the two UNTDID codes for genuine SELF-BILLING (§14(2) UStG):
 * 389 „self-billed invoice" and 261 „self-billed credit note". In those cases, the customer
 * issues the receipt — if one is in OUR inbox, our customer created it for OUR service, so it is
 * REVENUE.
 *
 * Deliberately a SEPARATE, much narrower set than `CANCELLATION_DOCUMENT_TYPE_CODES` above, and
 * explicitly WITHOUT the text patterns: that set also contains 381 („credit note") and
 * `/credit\s*note/i`, which represent the colloquial case — a supplier's invoice correction. It
 * remains a (negative) EXPENSE and never changes ledger. The cases are distinguished not by the
 * word „Gutschrift", but by WHO issued the document; only these two codes state that unambiguously.
 *
 * The set therefore supports only a SUGGESTION (plan `review-booking-target` T6) — it preselects
 * the checkbox but decides nothing.
 */
const SELF_BILLED_DOCUMENT_TYPE_CODES = new Set(['261', '389'])

export const isSelfBilledDocumentType = (documentType) => {
  return collectValues(documentType).some(
    value => SELF_BILLED_DOCUMENT_TYPE_CODES.has(`${value}`.trim()),
  )
}

/**
 * Every location where a receipt type may appear — review data first, then the persisted review
 * source, then the analysis in its various representations. Extracted so cancellation detection
 * and self-billing detection inspect the SAME candidates; two lists would create two truths about
 * the same receipt.
 */
export const getDocumentTypeCandidates = (file, reviewData = {}) => {
  const documentInvoiceDetails = file?.documentInvoiceDetails || {}
  const ocr = file?.metaData?.ocr || {}

  return [
    reviewData?.metaData?.documentType,
    documentInvoiceDetails.reviewSource?.documentType,
    ocr.documentType,
    ocr.document_type,
    ocr.documentTypeCode,
    ocr.document_type_code,
    ocr.invoiceType,
    ocr.invoice_type,
    ocr.invoiceTypeCode,
    ocr.invoice_type_code,
    ocr.creditNoteTypeCode,
    ocr.credit_note_type_code,
  ]
}

export const getDocumentInvoiceKind = (file, reviewData = {}) => {
  const documentTypeValues = getDocumentTypeCandidates(file, reviewData)

  if (documentTypeValues.some(isCancellationInvoiceDocumentType)) {
    return TAG_TYPES.CANCELLATION
  }

  if (file?.tags?.some(({ isSystem, tagType }) => isSystem && tagType === TAG_TYPES.CANCELLATION)) {
    return TAG_TYPES.CANCELLATION
  }

  return TAG_TYPES.INCOMING_INVOICE
}

export const getDocumentInvoiceKindLabel = (invoiceKind) => {
  return invoiceKind === TAG_TYPES.CANCELLATION
    ? TAG_LABELS.CANCELLATION
    : TAG_LABELS.INCOMING_INVOICE
}

// FLAG:useDocuAi — which „Art" (system tag) may offer a review entry point at all: incoming invoice
// AND cancellation invoice. A cancellation IS an invoice, and everything behind the entry point
// already supports it — the review page has its own title (`reviewCancellationInvoiceTitle`), and
// neither `/documents/review/:id` nor the server (`canReviewDocumentInvoice`) has a document-type
// gate; F18 merely hides „Ausgabe erstellen" there. Previously, each entry point checked
// INCOMING_INVOICE independently — while automatic classification itself assigns the other type
// (`invoiceAutoClassification.ts`: detectedDocumentKind 'credit-note' → SYSTEM_TAGS.CANCELLATION,
// “the detected type wins”). orgaMAX Web accordingly excluded the receipt from its own review
// through its own classification.
//
// ⚠ NOT symmetric with an incoming invoice: the „Stornorechnung" system tag ALSO appears on an
// outgoing cancellation invoice — `api/migration/migrations/20240821145839_addDefaultSystemTagsTo-
// Cancellations.js` attaches it to every `cancellations` row. There is no incoming-invoice review
// for those, so `type` excludes them (the same distinction already made by
// `redux/ducks/document/list.ts` for the scan entry). The question does not arise for incoming
// invoices: an outgoing invoice carries TAG_TYPES.INVOICE.
const OWN_OUTGOING_DOCUMENT_TYPES = [DocumentType.INVOICE, DocumentType.CANCELLATION]

const hasArt = (file, tagType) => (file?.tags || []).some((tag) => tag?.tagType === tagType)

export const isReviewableInvoiceArt = (file) => {
  if (hasArt(file, TAG_TYPES.INCOMING_INVOICE)) { return true }
  return hasArt(file, TAG_TYPES.CANCELLATION)
    && !OWN_OUTGOING_DOCUMENT_TYPES.includes(file?.type)
}

export const buildDocumentInvoiceReviewTags = (file, invoiceKind) => {
  const systemTagLabel = getDocumentInvoiceKindLabel(invoiceKind)
  const customTagLabels = (file?.tags || [])
    .filter(({ isSystem }) => !isSystem)
    .map(({ label }) => label)
    .filter(Boolean)

  return [systemTagLabel, ...customTagLabels]
}
